import { useEffect, useRef, useState } from 'react';
import { heroDemoCases, type HeroDemoCase } from '@/content/site';
import './HeroChatDemo.css';

const STEP_MS = 2600;
const PROCESSING_HOLD_MS = 4000;
const PAUSE_END_MS = 4800;
const CASE_PAUSE_MS = 1000;

function ChatAvatar({
  src,
  alt,
  variant,
}: {
  src: string;
  alt: string;
  variant: 'agent' | 'visitor';
}) {
  return (
    <img
      src={src}
      alt={alt}
      className={`hero-float__avatar hero-float__avatar--${variant}`}
      loading="lazy"
      width={28}
      height={28}
    />
  );
}

function ChatBubble({
  msg,
  demoCase,
  scriptIndex,
  isNewest,
}: {
  msg: HeroDemoCase['script'][number];
  demoCase: HeroDemoCase;
  scriptIndex: number;
  isNewest: boolean;
}) {
  const displayName =
    msg.speakerName ?? (msg.role === 'agent' ? demoCase.agentName : demoCase.visitorName);
  const avatarSrc =
    msg.speakerAvatarUrl ??
    (msg.role === 'agent' ? demoCase.agentAvatarUrl : demoCase.visitorAvatarUrl);

  return (
    <div
      key={`float-${scriptIndex}`}
      className={`hero-float hero-float--${msg.role}${
        isNewest ? ' hero-float--enter' : ''
      }${
        msg.accent ? ' hero-float--accent' : ''}${
        msg.processing ? ' hero-float--processing' : ''
      }${msg.humanHandoff ? ' hero-float--handoff' : ''}${
        msg.image ? ' hero-float--with-image' : ''
      }`}
    >
      <div className="hero-float__head">
        {msg.role === 'agent' ? (
          <ChatAvatar src={avatarSrc} alt={displayName} variant="agent" />
        ) : (
          <ChatAvatar
            src={demoCase.visitorAvatarUrl}
            alt={demoCase.visitorName}
            variant="visitor"
          />
        )}
        <span className="hero-float__name">{displayName}</span>
      </div>
      {msg.image ? (
        <img
          src={msg.image}
          alt={msg.imageAlt ?? ''}
          className="hero-float__img"
          loading="lazy"
        />
      ) : null}
      <p className="hero-float__text">{msg.text}</p>
    </div>
  );
}

export function HeroChatDemo() {
  const [caseIndex, setCaseIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const demoCase = heroDemoCases[caseIndex]!;
  const script = demoCase.script;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      const reduced = mq.matches;
      setReducedMotion(reduced);
      if (reduced) setVisibleCount(script.length);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [script.length]);

  useEffect(() => {
    setVisibleCount(0);
  }, [caseIndex]);

  useEffect(() => {
    if (reducedMotion) return;

    if (visibleCount >= script.length) {
      const t = window.setTimeout(() => {
        setVisibleCount(0);
        setCaseIndex((i) => (i + 1) % heroDemoCases.length);
      }, PAUSE_END_MS + CASE_PAUSE_MS);
      return () => window.clearTimeout(t);
    }

    const lastShown = visibleCount > 0 ? script[visibleCount - 1] : undefined;
    const delay = lastShown?.processing ? PROCESSING_HOLD_MS : STEP_MS;

    const t = window.setTimeout(() => {
      setVisibleCount((c) => c + 1);
    }, delay);
    return () => window.clearTimeout(t);
  }, [visibleCount, script.length, reducedMotion, caseIndex]);

  const display = script.slice(0, visibleCount);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.scrollTop = stage.scrollHeight;
  }, [visibleCount, caseIndex, display.length]);

  return (
    <div className="hero-chat-demo" aria-hidden>
      <div className="hero-chat-demo__glow" />
      <header className="hero-chat-demo__case">
        <h3 className="hero-chat-demo__case-name">{demoCase.agentName}</h3>
        <p className="hero-chat-demo__case-desc">{demoCase.description}</p>
      </header>
      <div className="hero-chat-demo__stage" ref={stageRef}>
        {display.map((msg, i) => {
          const scriptIndex = i;
          const isNewest = i === display.length - 1;
          return (
            <ChatBubble
              key={`${demoCase.id}-${scriptIndex}`}
              msg={msg}
              demoCase={demoCase}
              scriptIndex={scriptIndex}
              isNewest={isNewest}
            />
          );
        })}
      </div>
      <div className="hero-chat-demo__dots" role="tablist" aria-label="Casos de demo">
        {heroDemoCases.map((c, i) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            className={`hero-chat-demo__dot${i === caseIndex ? ' is-active' : ''}`}
            aria-selected={i === caseIndex}
            aria-label={c.agentName}
            onClick={() => {
              setCaseIndex(i);
              setVisibleCount(0);
            }}
          />
        ))}
      </div>
    </div>
  );
}
